import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { useUser } from '../../contexts/UserContext';
import { AlertCircle, Loader2, Mail } from 'lucide-react';
type AuthMode = 'signin' | 'signup' | 'reset';
interface AuthFormProps {
  onLoginSuccess?: () => void;
}
export function AuthForm({
  onLoginSuccess
}: AuthFormProps) {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const {
    signIn,
    signUp,
    resetPassword
  } = useUser();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === 'signin') {
        const {
          error
        } = await signIn(email, password);
        if (error) {
          setError(error.message);
        } else if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else if (mode === 'signup') {
        const {
          error
        } = await signUp(email, password);
        if (error) {
          setError(error.message);
        } else if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else if (mode === 'reset') {
        const {
          error
        } = await resetPassword(email);
        if (error) {
          setError(error.message);
        } else {
          setResetSent(true);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const renderForm = () => {
    if (resetSent) {
      return <div className="text-center p-4" data-id="element-47">
          <Mail className="mx-auto h-12 w-12 text-primary mb-4" data-id="element-48" />
          <h2 className="text-xl font-semibold mb-2" data-id="element-49">Check your email</h2>
          <p className="text-gray-600 mb-6" data-id="element-50">
            We've sent you a password reset link. Please check your inbox.
          </p>
          <Button variant="outline" onClick={() => {
          setResetSent(false);
          setMode('signin');
        }} className="w-full" data-id="element-51">
            Back to Sign In
          </Button>
        </div>;
    }
    return <form onSubmit={handleSubmit} className="space-y-4" data-id="element-52">
        {error && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm flex items-start" data-id="element-53">
            <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-54" />
            <span data-id="element-55">{error}</span>
          </div>}
        <div data-id="element-56">
          <label className="block text-sm font-medium mb-1" data-id="element-57">Email</label>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required data-id="element-58" />
        </div>
        {mode !== 'reset' && <div data-id="element-59">
            <div className="flex justify-between items-center mb-1" data-id="element-60">
              <label className="block text-sm font-medium" data-id="element-61">Password</label>
              {mode === 'signin' && <button type="button" onClick={() => setMode('reset')} className="text-xs text-primary hover:underline" data-id="element-62">
                  Forgot password?
                </button>}
            </div>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={mode === 'signup' ? 'Create a password' : 'Enter your password'} required data-id="element-63" />
          </div>}
        <Button type="submit" className="w-full" disabled={loading} data-id="element-64">
          {loading ? <>
              <Loader2 size={16} className="mr-2 animate-spin" data-id="element-65" />
              {mode === 'signin' ? 'Signing in...' : mode === 'signup' ? 'Creating account...' : 'Sending...'}
            </> : mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Reset Password'}
        </Button>
        <div className="text-center text-sm" data-id="element-66">
          {mode === 'signin' ? <p data-id="element-67">
              Don't have an account?{' '}
              <button type="button" onClick={() => setMode('signup')} className="text-primary hover:underline" data-id="element-68">
                Sign up
              </button>
            </p> : mode === 'signup' ? <p data-id="element-69">
              Already have an account?{' '}
              <button type="button" onClick={() => setMode('signin')} className="text-primary hover:underline" data-id="element-70">
                Sign in
              </button>
            </p> : <button type="button" onClick={() => setMode('signin')} className="text-primary hover:underline" data-id="element-71">
              Back to Sign In
            </button>}
        </div>
      </form>;
  };
  return <Card className="w-full max-w-md mx-auto" data-id="element-72">
      <CardContent className="p-6" data-id="element-73">
        <h2 className="text-2xl font-bold text-center mb-6" data-id="element-74">
          {mode === 'signin' ? 'Welcome Back' : mode === 'signup' ? 'Create an Account' : 'Reset Password'}
        </h2>
        {renderForm()}
      </CardContent>
    </Card>;
}